

// /**
//  * Topbar.tsx — sticky top bar with mobile menu toggle, breadcrumb, and user menu.
//  *
//  * UI POLISH changes:
//  *   • "Welcome back / email" copy replaced with a breadcrumb showing the current
//  *     page name. Derived from useLocation() + ALL_NAV_ITEMS (no API calls).
//  *     Falls back to the raw pathname segment if no match is found.
//  *   • Breadcrumb is hidden on mobile (sm:block) — same breakpoint as before.
//  *
//  * All existing functionality preserved exactly:
//  *   • SH-4 keyboard shortcut (? → /help-guide)
//  *   • Notification bell with unread count badge
//  *   • ThemeToggle, Help Guide icon button
//  *   • User avatar dropdown: name, email, role badge, My Profile, Sign out
//  *   • onMenuClick mobile hamburger
//  */
// import { Bell, ChevronRight, HelpCircle, Home, LogOut, Menu, User } from "lucide-react";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { useEffect } from "react";
// import { useQuery } from "@tanstack/react-query";
// import { useAuth } from "@/context/AuthContext";
// import { http } from "@/services/apiClient";
// import { Button } from "@/components/ui/button";
// import {
//   DropdownMenu,
//   DropdownMenuItem,
//   DropdownMenuLabel,
//   DropdownMenuSeparator,
//   DropdownMenuTrigger,
//   DropdownMenuContent,
// } from "@/components/ui/dropdown-menu";
// import {
//   Tooltip,
//   TooltipContent,
//   TooltipTrigger,
// } from "@/components/ui/tooltip";
// import { ThemeToggle } from "@/components/ThemeToggle";
// import { initials } from "@/lib/utils";
// import { Badge } from "@/components/ui/badge";
// import { ALL_NAV_ITEMS, PATH_ALIASES } from "@/lib/nav-config";

// /* ── Breadcrumb helpers ───────────────────────────────────────────────────── */

// /**
//  * Resolve the display label for the current pathname.
//  *
//  * Strategy:
//  *  1. Check PATH_ALIASES to normalise alias paths to canonical ones.
//  *  2. Exact match against ALL_NAV_ITEMS paths.
//  *  3. Prefix match (longest wins) — covers /campaigns/123 → "Campaigns".
//  *  4. Fall back to capitalising the last path segment.
//  */
// function usePageLabel(): string {
//   const location = useLocation();
//   const raw = location.pathname;

//   // Resolve alias
//   const canonical = PATH_ALIASES[raw] ?? raw;

//   // Exact match
//   const exact = ALL_NAV_ITEMS.find((item) => item.path === canonical);
//   if (exact) return exact.label;

//   // Longest prefix match
//   let best: { label: string; pathLen: number } | null = null;
//   for (const item of ALL_NAV_ITEMS) {
//     if (item.path === "/") continue; // skip root — too broad
//     if (canonical.startsWith(item.path)) {
//       if (!best || item.path.length > best.pathLen) {
//         best = { label: item.label, pathLen: item.path.length };
//       }
//     }
//   }
//   if (best) return best.label;

//   // Fallback: capitalise last segment
//   const segments = canonical.split("/").filter(Boolean);
//   const last = segments[segments.length - 1] ?? "";
//   return last
//     .split("-")
//     .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
//     .join(" ");
// }

// /* ── Topbar ───────────────────────────────────────────────────────────────── */

// export function Topbar({ onMenuClick }: { onMenuClick?: () => void }) {
//   const { user, logout, isAuthenticated } = useAuth();
//   const navigate = useNavigate();
//   const pageLabel = usePageLabel();
//   const isRoot = useLocation().pathname === "/";

//   // SH-4 — keyboard shortcut: press ? to open Help Guide
//   useEffect(() => {
//     function handleKey(e: KeyboardEvent) {
//       // Ignore when typing in an input, textarea, or select
//       const tag = (e.target as HTMLElement)?.tagName;
//       if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
//       if (e.key === "?") {
//         e.preventDefault();
//         navigate("/help-guide");
//       }
//     }
//     window.addEventListener("keydown", handleKey);
//     return () => window.removeEventListener("keydown", handleKey);
//   }, [navigate]);

//   // Notification bell — Help Guide §Compliance: "notification bell widget"
//   const { data: notifData } = useQuery({
//     queryKey: ["notifications", "unread-count"],
//     queryFn: () =>
//       http
//         .get<{ unread_count: number }>("/api/v1/notifications/unread-count")
//         .catch(() => ({ unread_count: 0 })),
//     enabled: isAuthenticated,
//     staleTime: 30_000,
//   });
//   const unreadCount = notifData?.unread_count ?? 0;

//   return (
//     <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-background px-4 sm:px-6">
//       {/* Left: mobile hamburger + breadcrumb */}
//       <div className="flex items-center gap-3">
//         <Tooltip>
//           <TooltipTrigger asChild>
//             <Button
//               variant="ghost"
//               size="icon"
//               className="md:hidden"
//               onClick={onMenuClick}
//               aria-label="Toggle navigation"
//             >
//               <Menu className="h-5 w-5" />
//             </Button>
//           </TooltipTrigger>
//           <TooltipContent>Toggle navigation</TooltipContent>
//         </Tooltip>

//         {/* Breadcrumb — replaces "Welcome back / email" copy */}
//         <nav aria-label="Breadcrumb" className="hidden sm:flex items-center gap-1.5 text-sm">
//           <Link
//             to="/"
//             className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
//             aria-label="Dashboard"
//           >
//             <Home className="h-3.5 w-3.5" />
//           </Link>
//           {!isRoot && (
//             <>
//               <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
//               <span className="font-medium text-foreground">{pageLabel}</span>
//             </>
//           )}
//         </nav>
//       </div>

//       {/* Right: role badge, help, bell, theme, user menu */}
//       <div className="flex items-center gap-3">
//         {user && (
//           <Badge variant="secondary" className="hidden sm:inline-flex">
//             {user.role.replace("_", " ")}
//           </Badge>
//         )}
//         <Tooltip>
//           <TooltipTrigger asChild>
//             <Button variant="ghost" size="icon" aria-label="Help guide" asChild>
//               <Link to="/help-guide">
//                 <HelpCircle className="h-5 w-5" />
//               </Link>
//             </Button>
//           </TooltipTrigger>
//           <TooltipContent>Help Guide — docs &amp; walkthroughs</TooltipContent>
//         </Tooltip>

//         {/* Notification bell — Help Guide §Compliance */}
//         <Tooltip>
//           <TooltipTrigger asChild>
//             <Button variant="ghost" size="icon" aria-label="Notifications" asChild>
//               <Link to="/notifications" className="relative">
//                 <Bell className="h-5 w-5" />
//                 {unreadCount > 0 && (
//                   <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
//                     {unreadCount > 9 ? "9+" : unreadCount}
//                   </span>
//                 )}
//               </Link>
//             </Button>
//           </TooltipTrigger>
//           <TooltipContent>
//             Notifications{unreadCount > 0 ? ` (${unreadCount} unread)` : ""}
//           </TooltipContent>
//         </Tooltip>

//         <ThemeToggle />

//         <DropdownMenu>
//           <Tooltip>
//             <TooltipTrigger asChild>
//               <DropdownMenuTrigger asChild>
//                 <Button variant="ghost" size="icon" aria-label="User menu">
//                   <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
//                     {initials(user?.name)}
//                   </span>
//                 </Button>
//               </DropdownMenuTrigger>
//             </TooltipTrigger>
//             <TooltipContent>Account &amp; sign out</TooltipContent>
//           </Tooltip>
//           <DropdownMenuContent align="end">
//             <DropdownMenuLabel>
//               <div className="flex flex-col">
//                 <span className="text-sm font-medium">{user?.name ?? "User"}</span>
//                 <span className="text-xs text-muted-foreground">{user?.email}</span>
//               </div>
//             </DropdownMenuLabel>
//             <DropdownMenuSeparator />
//             <DropdownMenuItem asChild>
//               <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
//                 <User className="h-4 w-4" /> My Profile
//               </Link>
//             </DropdownMenuItem>
//             <DropdownMenuSeparator />
//             <DropdownMenuItem onClick={() => void logout()} destructive>
//               <LogOut className="h-4 w-4" /> Sign out
//             </DropdownMenuItem>
//           </DropdownMenuContent>
//         </DropdownMenu>
//       </div>
//     </header>
//   );
// }


import { Bell, ChevronRight, HelpCircle, Home, LogOut, Menu, User } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { http } from "@/services/apiClient";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ThemeToggle } from "@/components/ThemeToggle";
import { initials } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { ALL_NAV_ITEMS, PATH_ALIASES } from "@/lib/nav-config";

/* ── Breadcrumb helpers ───────────────────────────────────────────────────── */

/**
 * Resolve the display label for the current pathname.
 *
 * Strategy:
 *  1. Check PATH_ALIASES to normalise alias paths to canonical ones.
 *  2. Exact match against ALL_NAV_ITEMS paths.
 *  3. Prefix match (longest wins) — covers /campaigns/123 → "Campaigns".
 *  4. Fall back to capitalising the last path segment.
 */
function usePageLabel(): string {
  const location = useLocation();
  const raw = location.pathname;

  // Resolve alias
  const canonical = PATH_ALIASES[raw] ?? raw;

  // Exact match
  const exact = ALL_NAV_ITEMS.find((item) => item.path === canonical);
  if (exact) return exact.label;

  // Longest prefix match
  let best: { label: string; pathLen: number } | null = null;
  for (const item of ALL_NAV_ITEMS) {
    if (item.path === "/") continue; // skip root — too broad
    if (canonical.startsWith(item.path)) {
      if (!best || item.path.length > best.pathLen) {
        best = { label: item.label, pathLen: item.path.length };
      }
    }
  }
  if (best) return best.label;

  // Fallback: capitalise last segment
  const segments = canonical.split("/").filter(Boolean);
  const last = segments[segments.length - 1] ?? "";
  return last
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/* ── Topbar ───────────────────────────────────────────────────────────────── */

export function Topbar({ onMenuClick }: { onMenuClick?: () => void }) {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const pageLabel = usePageLabel();
  const isRoot = useLocation().pathname === "/";

  // SH-4 — keyboard shortcut: press ? to open Help Guide
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      // Ignore when typing in an input, textarea, or select
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "?") {
        e.preventDefault();
        navigate("/help-guide");
      }
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [navigate]);

  // Notification bell — Help Guide §Compliance: "notification bell widget"
  const { data: notifData } = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: () =>
      http
        .get<{ unread_count: number }>("/api/v1/notifications/unread-count")
        .catch(() => ({ unread_count: 0 })),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
  const unreadCount = notifData?.unread_count ?? 0;

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-background px-4 sm:px-6">
      {/* Left: mobile hamburger + breadcrumb */}
      <div className="flex items-center gap-3">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={onMenuClick}
              aria-label="Toggle navigation"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Toggle navigation</TooltipContent>
        </Tooltip>

        {/* Breadcrumb — replaces "Welcome back / email" copy */}
        <nav aria-label="Breadcrumb" className="hidden sm:flex items-center gap-1.5 text-sm">
          <Link
            to="/"
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Dashboard"
          >
            <Home className="h-3.5 w-3.5" />
          </Link>
          {!isRoot && (
            <>
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
              <span className="font-medium text-foreground">{pageLabel}</span>
            </>
          )}
        </nav>
      </div>

      {/* Right: role badge, help, bell, theme, user menu */}
      <div className="flex items-center gap-3">
        {user && (
          <Badge variant="secondary" className="hidden sm:inline-flex">
            {user.role.replace("_", " ")}
          </Badge>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Help guide" asChild>
              <Link to="/help-guide">
                <HelpCircle className="h-5 w-5" />
              </Link>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Help Guide — docs &amp; walkthroughs</TooltipContent>
        </Tooltip>

        {/* Notification bell — Help Guide §Compliance */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Notifications" asChild>
              <Link to="/notifications" className="relative">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Link>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Notifications{unreadCount > 0 ? ` (${unreadCount} unread)` : ""}
          </TooltipContent>
        </Tooltip>

        <ThemeToggle />

        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="User menu">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                    {initials(user?.name)}
                  </span>
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>Account &amp; sign out</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{user?.name ?? "User"}</span>
                {/* Username login: show the sign-in username when it differs from the email. */}
                {user?.username && user.username !== user.email?.toLowerCase() && (
                  <span className="font-mono text-xs text-muted-foreground">{user.username}</span>
                )}
                <span className="text-xs text-muted-foreground">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                <User className="h-4 w-4" /> My Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => void logout()} destructive>
              <LogOut className="h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
